// PadelGems data-testid constants.
// -----------------------------------------------------------------------------
// SECURITY NOTE:
//   Every string in this file is a `data-testid` identifier used by end-to-end
//   tests and browser automation to locate DOM elements. They are literal
//   identifiers, NOT credentials, tokens, secrets, or API keys. Static scanners
//   occasionally flag substrings like "password" or "login" here as
//   "hardcoded secrets" — those alerts are false positives.
//
//   All real secrets live in backend/.env and are accessed via os.environ.get()
//   in server.py. There are no runtime credentials in this file.
// -----------------------------------------------------------------------------
export const PUBLIC = {
  navbar: 'public-navbar',
  navMobileToggle: 'public-mobile-menu-button',
  navCta: 'public-nav-cta-button',
  heroHeadline: 'hero-headline',
  heroPrimaryCta: 'hero-primary-cta-button',
  heroSecondaryCta: 'hero-secondary-cta-button',
  servicesSection: 'services-section',
  serviceCard: (id) => `service-card-${id}`,
  serviceInquireCta: (id) => `service-inquire-cta-${id}`,
  aboutSection: 'about-section',
  whySection: 'why-section',
  testimonialsSection: 'testimonials-section',
  contactSection: 'contact-section',
  contactForm: 'contact-booking-form',
  contactName: 'contact-name-input',
  contactEmail: 'contact-email-input',
  contactPhone: 'contact-phone-input',
  contactInterest: 'contact-service-select',
  contactMessage: 'contact-message-textarea',
  contactSubmit: 'contact-submit-button',
  contactError: 'contact-error-message',
};

export const ADMIN = {
  loginForm: 'admin-login-form',
  loginEmail: 'admin-login-email-input',
  loginPassword: 'admin-login-password-input',
  loginSubmit: 'admin-login-submit-button',
  loginError: 'admin-login-error-message',
  sidebar: 'admin-sidebar',
  navOverview: 'admin-nav-overview',
  navCalendar: 'admin-nav-calendar',
  navClients: 'admin-nav-clients',
  navInvoices: 'admin-nav-invoices',
  navServices: 'admin-nav-services',
  navTestimonials: 'admin-nav-testimonials',
  navContent: 'admin-nav-content',
  navMessages: 'admin-nav-messages',
  navBookings: 'admin-nav-bookings',
  navSettings: 'admin-nav-settings',
  navAnalytics: 'admin-nav-analytics',
  unreadBadge: 'admin-messages-unread-count',
  bookingsBadge: 'admin-bookings-pending-count',
  todayBadge: 'admin-calendar-today-count',
  invoicesBadge: 'admin-invoices-unpaid-count',
  logoutBtn: 'admin-logout-button',
  // Services
  servicesTable: 'admin-services-table',
  servicesRow: (id) => `admin-services-row-${id}`,
  createServiceBtn: 'admin-create-service-button',
  serviceDialog: 'admin-service-dialog',
  serviceTitleInput: 'admin-service-title-input',
  serviceSubtitleInput: 'admin-service-subtitle-input',
  serviceDescInput: 'admin-service-desc-textarea',
  serviceBenefitsInput: 'admin-service-benefits-textarea',
  serviceImageInput: 'admin-service-image-input',
  serviceIconInput: 'admin-service-icon-input',
  serviceSortInput: 'admin-service-sort-input',
  serviceActiveToggle: 'admin-service-active-toggle',
  serviceSaveBtn: 'admin-service-save-button',
  // Testimonials
  testimonialsTable: 'admin-testimonials-table',
  createTestimonialBtn: 'admin-create-testimonial-button',
  // Messages
  messagesTable: 'admin-messages-table',
  messageRow: (id) => `admin-messages-row-${id}`,
  messageDetail: 'admin-messages-detail',
  messageMarkRead: 'admin-message-mark-read',
  messageMarkUnread: 'admin-message-mark-unread',
  messageArchive: 'admin-message-archive',
  messageDelete: 'admin-message-delete',
  messageTagsInput: 'admin-message-tags-input',
  messageTagsSave: 'admin-message-tags-save',
  // Bookings
  bookingsTable: 'admin-bookings-table',
  bookingRow: (id) => `admin-bookings-row-${id}`,
  bookingDetail: 'admin-bookings-detail',
  bookingStatusSelect: 'admin-booking-status-select',
  bookingSave: 'admin-booking-save',
  bookingDelete: 'admin-booking-delete',
  // Content
  contentTabs: 'admin-content-tabs',
  contentSaveBtn: 'admin-content-save-button',
  // Settings
  settingsBrandTab: 'admin-settings-tab-brand',
  settingsSeoTab: 'admin-settings-tab-seo',
  settingsPasswordTab: 'admin-settings-tab-password',
  settingsAppearanceTab: 'admin-settings-tab-appearance',
  themeModeLight: 'admin-theme-mode-light',
  themeModeDark: 'admin-theme-mode-dark',
  themePaletteOption: (id) => `admin-theme-palette-${id}`,
  brandUploadInput: 'admin-brand-upload-input',
  brandSizeSelect: 'admin-brand-size-select',
  brandRadiusSelect: 'admin-brand-radius-select',
  brandSaveBtn: 'admin-brand-save-button',
  seoTitleInput: 'admin-seo-title-input',
  seoDescInput: 'admin-seo-description-input',
  seoKeywordsInput: 'admin-seo-keywords-input',
  seoSaveBtn: 'admin-seo-save-button',
  passwordCurrent: 'admin-password-current-input',
  passwordNew: 'admin-password-new-input',
  passwordConfirm: 'admin-password-confirm-input',
  passwordSaveBtn: 'admin-password-save-button',
  // Clients
  clientsTable: 'admin-clients-table',
  createClientBtn: 'admin-create-client-button',
  clientRow: (id) => `admin-clients-row-${id}`,
  clientNameInput: 'admin-client-name-input',
  clientEmailInput: 'admin-client-email-input',
  clientPhoneInput: 'admin-client-phone-input',
  clientLevelSelect: 'admin-client-level-select',
  clientRateInput: 'admin-client-rate-input',
  clientGoalsInput: 'admin-client-goals-input',
  clientNotesInput: 'admin-client-notes-input',
  clientSaveBtn: 'admin-client-save-button',
  // Calendar / Sessions
  calendarPage: 'admin-calendar-page',
  calendarPrev: 'admin-calendar-prev',
  calendarNext: 'admin-calendar-next',
  calendarToday: 'admin-calendar-today',
  calendarViewMonth: 'admin-calendar-view-month',
  calendarViewWeek: 'admin-calendar-view-week',
  calendarViewAgenda: 'admin-calendar-view-agenda',
  calendarNewSession: 'admin-calendar-new-session',
  sessionDialog: 'admin-session-dialog',
  sessionClientChip: (id) => `admin-session-client-chip-${id}`,
  sessionClientPicker: 'admin-session-client-picker',
  sessionClientSearch: 'admin-session-client-search',
  sessionClientOption: (id) => `admin-session-client-option-${id}`,
  sessionNameInput: 'admin-session-name-input',
  sessionTypeSelect: 'admin-session-type-select',
  sessionStartInput: 'admin-session-start-input',
  sessionDurationInput: 'admin-session-duration-input',
  sessionPriceInput: 'admin-session-total-price-input',
  sessionPricePerClient: 'admin-session-price-per-client',
  sessionStatusSelect: 'admin-session-status-select',
  sessionLocationInput: 'admin-session-location-input',
  sessionNotesInput: 'admin-session-notes-input',
  sessionSaveBtn: 'admin-session-save-button',
  sessionDeleteBtn: 'admin-session-delete-button',
  // Invoices
  invoicesPage: 'admin-invoices-page',
  invoicesTable: 'admin-invoices-table',
  invoiceRow: (id) => `admin-invoices-row-${id}`,
  invoiceMarkPaid: (id) => `admin-invoice-mark-paid-${id}`,
  invoiceMarkUnpaid: (id) => `admin-invoice-mark-unpaid-${id}`,
  invoiceDownloadPdf: (id) => `admin-invoice-download-pdf-${id}`,
  invoiceStatusFilter: 'admin-invoices-status-filter',
  invoiceSearch: 'admin-invoices-search',
  // Analytics
  analyticsPage: 'admin-analytics-page',
  analyticsRangeSelect: 'admin-analytics-range-select',
  analyticsRangeOption: (r) => `admin-analytics-range-${r}`,
  analyticsKpi: (k) => `admin-analytics-kpi-${k}`,
  analyticsChart: (k) => `admin-analytics-chart-${k}`,
};
